import {
  RadioGroupContext,
  requiredRule,
  useFormControl,
  useRadioInput,
} from "@mongez/react-form";
import { useEffect, useId } from "react";
import { cn } from "~/shared/lib";
import type { BaseInputProps } from "./BaseInput";
import { ErrorInput } from "./ErrorInput";
import { LabelInput } from "./LabelInput";

export type RadioInputPropsType = {
  children: string | React.ReactNode;
  defaultValue: string | boolean | number;
  classes?: {
    wrapper?: string;
    label?: string;
    input?: string;
  };
};

export function RadioInput({
  children,
  defaultValue,
  classes,
}: RadioInputPropsType) {
  const { isSelected, changeValue } = useRadioInput(defaultValue);

  const id = useId();

  return (
    <div
      className={cn(
        "flex w-[200px] flex-row-reverse items-center justify-end rounded-full border px-6",
        isSelected ? "border-primary-default" : "border-black",
        classes?.wrapper,
      )}
    >
      <label
        htmlFor={id}
        className={cn(
          "w-full cursor-pointer px-3 py-3 select-none ltr:text-left rtl:text-right",
          isSelected ? "font-normal" : "font-light",
          classes?.label,
        )}
      >
        {children}
      </label>

      <div className="flex h-5 w-5 items-center justify-center">
        <input
          type="radio"
          id={id}
          name={defaultValue.toString()}
          value={defaultValue.toString()}
          checked={isSelected}
          onChange={changeValue}
          className="hidden appearance-none"
        />
        <label
          htmlFor={id}
          className={cn(
            "hover:bg-primary-default flex size-5 cursor-pointer items-center justify-center rounded-full border border-black text-white transition-colors duration-300",
            isSelected && "bg-primary-default",
            classes?.input,
          )}
        >
          {isSelected && (
            <div className="bg-primary h-full w-full rounded-full border-4 border-white"></div>
          )}
        </label>
      </div>
    </div>
  );
}

/////////////////////////////////////////////////

export type RadioGroupProps = BaseInputProps;

export const RadioGroup = ({
  id,
  children,
  label,
  classes,
  ...props
}: RadioGroupProps) => {
  const { value, changeValue, error } = useFormControl({
    ...props,
    rules: [requiredRule],
  });

  useEffect(() => {
    if (props.defaultValue) {
      changeValue(props.defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.defaultValue]);

  useEffect(() => {
    if (props.value !== undefined && props.value !== value) {
      changeValue(props.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, value]);

  return (
    <RadioGroupContext.Provider
      value={{
        value,
        changeValue,
      }}
    >
      <div id={id} className={cn("flex flex-col gap-5", classes?.wrapper)}>
        <LabelInput
          htmlFor={id}
          required={props.required}
          className={cn(classes?.label, error && "text-red-500")}
        >
          {label}
        </LabelInput>

        <div
          className={cn(
            "flex flex-row flex-wrap gap-5 sm:flex-nowrap",
            classes?.input,
          )}
        >
          {children}
        </div>
        <ErrorInput className={classes?.error}>{error}</ErrorInput>
      </div>
    </RadioGroupContext.Provider>
  );
};
